import { useState } from "react";
import {
  archiveTemplate,
  saveTemplate,
  type SmsTemplate,
} from "../../services/sms.service";

interface Props {
  templates: SmsTemplate[];
  onSaved: (template: SmsTemplate) => void;
  onArchived: (id: string) => void;
  onClose: () => void;
}

/** Saved templates and send drafts deliberately have separate state and save actions. */
export default function SmsTemplateManager({
  templates,
  onSaved,
  onArchived,
  onClose,
}: Props) {
  const [editing, setEditing] = useState<SmsTemplate | null>(null);
  const [folder, setFolder] = useState("");
  const [folderFilter, setFolderFilter] = useState("__all");
  const folders = [
    ...new Set(
      templates.map((t) => t.folder_name).filter((v): v is string => !!v),
    ),
  ].sort();
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [archiveId, setArchiveId] = useState<string | null>(null);

  function edit(t: SmsTemplate | null) {
    setEditing(t);
    setName(t?.name ?? "");
    setFolder(t?.folder_name ?? (folderFilter === "__all" ? "" : folderFilter));
    setBody(t?.body ?? "");
    setDescription(t?.description ?? "");
    setError(null);
    setArchiveId(null);
  }
  async function save() {
    setBusy(true);
    setError(null);
    try {
      const saved = await saveTemplate(
        {
          name,
          body,
          description: description || null,
          folder_name: folder || null,
        },
        editing ?? undefined,
      );
      onSaved(saved);
      edit(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  async function archive(id: string) {
    setBusy(true);
    setError(null);
    try {
      await archiveTemplate(id);
      onArchived(id);
      if (editing?.id === id) edit(null);
      setArchiveId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section
      aria-label="Manage SMS templates"
      className="space-y-3 rounded-lg border p-3"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-medium">Manage SMS templates</h3>
        <button
          type="button"
          disabled={busy}
          onClick={onClose}
          className="text-sm text-blue-700"
        >
          Done
        </button>
      </div>
      <p className="text-xs text-gray-600">
        Templates are shared with other agents. Saving here updates the
        template; editing a message for a send does not.
      </p>
      <label className="block text-sm">
        Browse template folder
        <select
          value={folderFilter}
          onChange={(e) => setFolderFilter(e.target.value)}
          className="ml-2 rounded border p-1"
        >
          <option value="__all">All folders</option>
          <option value="">Unfiled</option>
          {folders.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      </label>
      {templates
        .filter(
          (t) =>
            folderFilter === "__all" || (t.folder_name ?? "") === folderFilter,
        )
        .map((t) => (
          <div key={t.id} className="flex items-center gap-2 text-sm">
            <span className="flex-1">{t.name}</span>
            <button
              type="button"
              disabled={busy}
              onClick={() => edit(t)}
              className="text-blue-700"
              aria-label={`Edit ${t.name}`}
            >
              Edit
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setArchiveId(t.id)}
              className="text-red-700"
              aria-label={`Archive ${t.name}`}
            >
              Archive
            </button>
            {archiveId === t.id && (
              <span>
                Remove from shared templates?{" "}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void archive(t.id)}
                  className="text-red-700"
                >
                  Confirm archive
                </button>{" "}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setArchiveId(null)}
                >
                  Keep
                </button>
              </span>
            )}
          </div>
        ))}
      <fieldset disabled={busy} className="space-y-2">
        <legend className="text-sm font-medium">
          {editing ? "Edit saved template" : "New template"}
        </legend>
        <label className="block text-sm">
          Folder
          <input
            list="sms-template-folders"
            value={folder}
            onChange={(e) => setFolder(e.target.value)}
            maxLength={120}
            placeholder="Unfiled"
            className="mt-1 w-full rounded border p-2"
          />
          <datalist id="sms-template-folders">
            {folders.map((f) => (
              <option key={f} value={f} />
            ))}
          </datalist>
        </label>
        <label className="block text-sm">
          Template name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={255}
            className="mt-1 w-full rounded border p-2"
          />
        </label>
        <label className="block text-sm">
          Description (optional)
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="mt-1 w-full rounded border p-2"
          />
        </label>
        <label className="block text-sm">
          Template message
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={10000}
            rows={4}
            className="mt-1 w-full rounded border p-2"
          />
        </label>
        <p className="text-xs text-gray-600">
          Use [First Name], [Agent Name], [Plan Name], and [Renewal Date] to
          prefill contact details where available. Agents can edit the draft.
          The opt-out footer is added automatically when sent.
        </p>
        <button
          type="button"
          disabled={!name.trim() || !body.trim()}
          onClick={() => void save()}
          className="rounded bg-blue-600 px-3 py-2 text-sm text-white disabled:bg-gray-300"
        >
          {busy ? "Saving…" : "Save template"}
        </button>{" "}
        {editing && (
          <button type="button" onClick={() => edit(null)} className="text-sm">
            Cancel edit
          </button>
        )}
      </fieldset>
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
    </section>
  );
}
